import React, { useState, useEffect, MouseEvent, useRef } from 'react';
import { buildClassName } from 'utils/build-class-name';
import { useModalContext, useSavedListingContext, useThemeContext, useUserContext } from 'contexts';
import HollowHeartIcon from 'components/icon/hollow-heart-icon';
import styles from './style.module.scss';
import { dynamicTestIds, listingCardIds } from 'constants/test-constants';
import { ThemeNames } from 'types/themes';
import { trackEvent } from 'utils/google-tag-manager';

type FavouriteToggle = {
  listingId: number;
  addressSlug?: string;
  isInCarousel?: boolean;
  isSmallCard?: boolean;
  isModal?: boolean;
  isHeartExperiment?: boolean;
  favouriteEventTracker?: () => void;
};

type FavouriteToggleBaseProps = {
  toggleFavouriteState: () => void;
  isFavourite: boolean;
  isInCarousel?: boolean;
  isSmallCard?: boolean;
  isLoading?: boolean;
};

export const FavouriteToggleBase = ({ isInCarousel, isSmallCard, isFavourite, toggleFavouriteState, isLoading }: FavouriteToggleBaseProps) => {
  const containerClassName = buildClassName(isInCarousel && styles.component, isSmallCard && styles['small-card'], styles['favourite-wrapper'], isInCarousel && styles['address-page-icon']);
  const dynamicTestid = isFavourite ? dynamicTestIds.favourite : dynamicTestIds.notFavourite;

  const handleOnCLick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    toggleFavouriteState?.();
  };
  const { themeName } = useThemeContext();
  const isExpTheme = themeName && themeName !== ThemeNames.ZOOCASA;

  return (
    <div className={containerClassName} onClick={handleOnCLick} data-testid={listingCardIds.favouriteToggle} data-dynamic-testid={dynamicTestid}>
      {isLoading ? (
        <div className={styles.spinnerWrapper}>
          <div className={styles.spinner} />
        </div>
      ) : (
        <HollowHeartIcon className={buildClassName(styles.favourite, isFavourite && styles.active, isExpTheme && styles.exp)} onClick={handleOnCLick} />
      )}
    </div>
  );
};

export default function FavouriteToggle({ listingId, addressSlug, isInCarousel, isSmallCard, isModal = false, isHeartExperiment = false, favouriteEventTracker }: FavouriteToggle) {
  const [isFavourite, setIsFavourite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { openModal } = useModalContext();
  const { isAuthenticated } = useUserContext();
  const { savedListings, addSavedListing, removeSavedListing, isSavedListing } = useSavedListingContext();
  const hasBeenClicked = useRef(false);

  useEffect(() => {
    setIsFavourite(isSavedListing(addressSlug, listingId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedListings, addressSlug, listingId]);

  const toggleFavouriteState = () => {
    if (isLoading) return;

    if (!hasBeenClicked.current && isHeartExperiment) {
      trackEvent('XpA_ListingMHeartButClk');
      hasBeenClicked.current = true;
    }

    if (isAuthenticated) {
      setIsLoading(true);

      if (isFavourite) {
        // When we want to remove the listing we need to have the slug
        // Listing ID of the current listing might not be the same as the listing ID of the original saved listing in the table.
        // So we do not have slug provided we need to get slug from the saved listings array.
        if (!addressSlug) {
          const originalSlug = savedListings.find(item => item.listingId === listingId)?.addressSlug;
          removeSavedListing(originalSlug);
          // Set timeout to ensure the spinner shows for at least a moment
          setTimeout(() => setIsLoading(() => false), 500);
        } else {
          removeSavedListing(addressSlug);
          setTimeout(() => setIsLoading(() => false), 500);
        }
      } else {
        addSavedListing({ listingId, addressSlug, isModal });
        setTimeout(() => setIsLoading(() => false), 500);
        favouriteEventTracker?.();
      }
    } else {
      openModal('login-registration', {
        callback: () => {
          addSavedListing({ listingId, addressSlug, isModal });
          setTimeout(() => setIsLoading(() => false), 500);
        },
      });
    }
  };

  return (
    <>
      <FavouriteToggleBase
        isFavourite={isFavourite}
        isInCarousel={isInCarousel}
        isSmallCard={isSmallCard}
        toggleFavouriteState={toggleFavouriteState}
        isLoading={isLoading}
      />
    </>
  );
}
